import type { IHttpClient } from '@/shared/types/api';
import { ApiError } from '@/shared/types/api';
import type { AuthenticateUserRequest, AuthenticateUserResult, CurrentUserDto, IAuthService, LoginResult } from '@/shared/types/auth';
import apiClient from '@/infra/api/ApiClient';

class AuthServiceImpl implements IAuthService {
  constructor(private readonly http: IHttpClient) {}

  async login(params: AuthenticateUserRequest): Promise<LoginResult> {
    try {
      const response = await this.http.post<CurrentUserDto>(
        '/auth/login',
        params
      );

      if (response.status !== 200) {
        return {
          succeeded: false,
          errors: ['Login failed'],
        };
      }

      const user = response.data;
      if (!user) {
        return {
          succeeded: false,
          errors: ['Invalid response: missing user data'],
        };
      }

      return {
        succeeded: true,
        user,
        errors: [],
      };
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        return {
          succeeded: false,
          errors: ['Invalid username or password.'],
        };
      }
      return {
        succeeded: false,
        errors: ['Something went wrong. Please try again later.'],
      };
    }
  }

  async logout(): Promise<void> {
    await this.http.post('/auth/logout', {});
  }

  async getCurrentUser(): Promise<CurrentUserDto | null> {
    try {
      const response = await this.http.get<CurrentUserDto>('/auth/me');
      if (response.status === 200 && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        return null;
      }
      throw error;
    }
  }
}

export const authService = new AuthServiceImpl(apiClient);
